// Regras puras da tela de Leads (/leads) e dos seus modais.
import { i18n } from "../../translate/i18n";

export const STATUS_BUSCA = ["queued", "running", "processing", "done", "failed", "cancelled"];

export const statusLabel = (status) =>
  i18n.t(`aeros.leads.status.${STATUS_BUSCA.includes(status) ? status : "unknown"}`);

export const statusTone = (status) => {
  if (status === "done") return "success";
  if (status === "failed") return "danger";
  if (status === "running" || status === "processing") return "info";
  if (status === "queued") return "warning";
  return "neutral";
};

export const parseValor = (valor) => {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
};

export const formatBRL = (valor, casas = 2) =>
  parseValor(valor).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", minimumFractionDigits: casas, maximumFractionDigits: casas,
  });

export const formatMilhar = (n) => Number(n || 0).toLocaleString("pt-BR");

export const precoPorLead = (pacote) => {
  const qtd = Number(pacote?.quantidade) || 0;
  if (qtd <= 0) return null;
  return parseValor(pacote?.valor) / qtd;
};

export const formatPrecoUnitario = (valor) =>
  valor === null || valor === undefined
    ? null
    : Number(valor).toLocaleString("pt-BR", {
        style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 3,
      });

export const bestValuePackId = (pacotes = []) => {
  const validos = pacotes.map((p) => ({ id: p.id, unit: precoPorLead(p) })).filter((p) => p.unit !== null);
  if (validos.length < 2) return null;
  return validos.reduce((melhor, p) => (p.unit < melhor.unit ? p : melhor)).id;
};

export const purchaseSummary = (pacote, saldo) => {
  if (!pacote) return null;
  const quantity = Number(pacote.quantidade) || 0;
  const total = parseValor(pacote.valor);
  const s = Number(saldo);
  return {
    total,
    quantity,
    unit: precoPorLead(pacote),
    newBalance: saldo === null || saldo === undefined || !Number.isFinite(s) ? null : s + quantity,
  };
};

export const COLUNAS_LEAD = [
  { key: "nome", i18n: "aeros.leads.lead.name" },
  { key: "telefone", i18n: "aeros.leads.lead.phone" },
  { key: "temWhatsapp", i18n: "aeros.leads.lead.whatsapp" },
  { key: "email", i18n: "aeros.leads.lead.email" },
  { key: "redes", i18n: "aeros.leads.lead.social" },
  { key: "endereco", i18n: "aeros.leads.lead.address" },
  { key: "site", i18n: "aeros.leads.lead.site" },
  { key: "notaGoogle", i18n: "aeros.leads.lead.rating" },
  { key: "numeroAvaliacoes", i18n: "aeros.leads.lead.reviews" },
  { key: "linkMaps", i18n: "aeros.leads.lead.maps" },
  { key: "observacoes", i18n: "aeros.leads.lead.notes" },
];

export const COLUNAS_PADRAO = ["nome", "telefone", "temWhatsapp", "email", "redes", "notaGoogle", "numeroAvaliacoes"];

// "nome" nunca some: sem ele a linha não identifica ninguém.
export const colunasVisiveis = (salvas) => {
  if (!Array.isArray(salvas)) return COLUNAS_PADRAO;
  const validas = COLUNAS_LEAD.map((c) => c.key);
  const filtradas = salvas.filter((k) => validas.includes(k));
  return filtradas.includes("nome") ? filtradas : ["nome", ...filtradas];
};

export const estimativaBusca = (quantidade, disponivel) => {
  const q = Number(quantidade);
  const d = Number(disponivel) || 0;
  const ok = Number.isInteger(q) && q > 0 && q <= d;
  return { ok, quantidade: Number.isFinite(q) ? q : 0, disponivel: d };
};

// Os links do lead vêm do scraper (conteúdo de terceiro) e podem estar
// gravados desde antes do saneamento no backend: só http(s) vira href.
export const urlSegura = (valor) => {
  if (!valor) return null;
  try {
    const { protocol } = new URL(valor);
    return protocol === "http:" || protocol === "https:" ? valor : null;
  } catch (e) {
    return null;
  }
};

export const formatTelefone = (e164) => {
  const d = String(e164 || "").replace(/\D/g, "");
  if (!d) return "";
  const semDdi = d.startsWith("55") ? d.slice(2) : d;
  const ddd = semDdi.slice(0, 2);
  const resto = semDdi.slice(2);
  if (resto.length === 9) return `(${ddd}) ${resto.slice(0, 5)}-${resto.slice(5)}`;
  if (resto.length === 8) return `(${ddd}) ${resto.slice(0, 4)}-${resto.slice(4)}`;
  return d;
};

export const formatNota = (nota) => {
  if (nota === null || nota === undefined || nota === "") return "—";
  const n = Number(nota);
  return Number.isFinite(n) ? n.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : "—";
};

export const whatsappTone = (temWhatsapp) => {
  if (temWhatsapp === true) return "yes";
  if (temWhatsapp === false) return "no";
  return "unknown";
};

export const entregaParcial = (busca) =>
  busca?.status === "done" && Number(busca.quantidadeEntregue) < Number(busca.quantidadeSolicitada);

const observacoesDoLead = (l) =>
  [
    l.categoria && `${i18n.t("aeros.leads.lead.notesCategory")}: ${l.categoria}`,
    l.horario && `${i18n.t("aeros.leads.lead.notesHours")}: ${l.horario}`,
    l.descricao && `${i18n.t("aeros.leads.lead.notesDescription")}: ${l.descricao}`,
  ]
    .filter(Boolean)
    .join(" | ");

const redesDoLead = (l) => [l.instagram, l.facebook, l.linkedin].filter(Boolean).join(" ");

export const valorColuna = (lead, key) => {
  if (key === "telefone") return formatTelefone(lead.telefone);
  if (key === "temWhatsapp") return i18n.t(`aeros.leads.whatsapp.${whatsappTone(lead.temWhatsapp)}`);
  if (key === "notaGoogle") return formatNota(lead.notaGoogle);
  if (key === "redes") return redesDoLead(lead);
  if (key === "observacoes") return observacoesDoLead(lead);
  const v = lead[key];
  return v === null || v === undefined ? "" : String(v);
};

const csvCampo = (v) => {
  const s = String(v ?? "");
  // Excel/Sheets interpretam célula iniciada com = + - @ tab ou CR como
  // fórmula: prefixa com aspas simples para neutralizar (CSV injection).
  const seguro = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return `"${seguro.replace(/"/g, '""')}"`;
};

export const linhasCsv = (leads = [], colunasKeys = COLUNAS_PADRAO) => {
  const colunas = COLUNAS_LEAD.filter((c) => colunasKeys.includes(c.key));
  const cabecalho = colunas.map((c) => i18n.t(c.i18n)).join(";");
  const linhas = leads.map((l) => colunas.map((c) => csvCampo(valorColuna(l, c.key))).join(";"));
  return `﻿${[cabecalho, ...linhas].join("\n")}`;
};
